<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ContractServiceFee',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    oid: {
      type: String,
      required: false,
    },
    resource: {
      type: Object,
      required: false,
    },
    noHeader: {
      type: Boolean,
      required: false,
    },
    noFooter: {
      type: Boolean,
      required: false,
    },
    displayParent: {
      type: Boolean,
      required: false,
      default: true,
    },
    inlineCommands: {
      type: Boolean,
      required: false,
      default: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const getServiceDescription = (...args) => {
    const [data] = args;
    const result = [];

    if (data.contract) {
      result.push(data.contract.contract_number);
    }
    if (data.contract_service) {
      result.push(data.contract_service.name);
    }
    if (result.length) {
      return result.join(' / ');
    }
  };

  const formId = ref(null);
  const formName = ref('ContractServiceFee');
  const item = ref(null);
  const serviceDescription = ref(null);
  const labelAlign = ref('right');
  const billingCategories = ref(null);
  const messages = ref({
    addService: {
      title: $fn.value.pgettext('Contract', 'Add Service'),
    },
    editService: {
      title: $fn.value.pgettext('Contract', 'Edit Service'),
    },
    addServiceFee: {
      title: $fn.value.pgettext('Contract', 'Add Service Fee'),
    },
    editServiceFee: {
      title: $fn.value.pgettext('Contract', 'Edit Service Fee'),
    },
  });
  const currencies = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    noPrimaryButton: true,
    closeButtonText: $fn.value.gettext('Close'),
    size: 'lg',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    formName: formName.value,
    item: item.value,
    serviceDescription: serviceDescription.value,
    labelAlign: labelAlign.value,
    billingCategories: billingCategories.value,
    messages: messages.value,
    currencies: currencies.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = async () => {
    return props.oid
      ? $fn.value.fetch('read_contract_service_fee', {
          parameters: { id: props.oid },
        })
      : props.resource;
  };
  const $$load_serviceDescription = async () => {
    return getServiceDescription(item.value);
  };
  const $$load_billingCategories = async () => {
    return $fn.value.fetch('get_tags', {
      parameters: { tag_type: 'billing_category' },
    });
  };
  const $$load_currencies = async () => {
    return $fn.value.fetch('list_currency');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'formName' },
      { name: 'item' },
      { name: 'serviceDescription', dependsOn: ['item'] },
      { name: 'labelAlign' },
      { name: 'billingCategories' },
      { name: 'messages' },
      { name: 'currencies' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      formId: $$load_formId,
      item: $$load_item,
      serviceDescription: $$load_serviceDescription,
      billingCategories: $$load_billingCategories,
      currencies: $$load_currencies,
    },
    ux: {},
    dataRefs: {
      formId,
      formName,
      item,
      serviceDescription,
      labelAlign,
      billingCategories,
      messages,
      currencies,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isReadonly = computed(() => {
    if (loaded.value) {
      return item.value && item.value.isReadonly;
    }
    return null;
  });
  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [
          serviceDescription.value,
          $fn.value.pgettext('ContractServiceFee', 'fee'),
        ],
        icon: $config.value.contract.icon,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );
  watch(
    () => props.resource,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('resource');
    }
  );
  watch(
    () => props.noHeader,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('noHeader');
    }
  );
  watch(
    () => props.noFooter,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('noFooter');
    }
  );
  watch(
    () => props.displayParent,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('displayParent');
    }
  );
  watch(
    () => props.inlineCommands,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('inlineCommands');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_2hhcw6ndu_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_1lez5ipyr_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };
  const inst_evh_1040167445267876_success_c3ud5alwf_getWhen = async (
    $event
  ) => {
    return props.modal;
  };
  const act_1040167445267876_crud_hx79fb6da_getActionArgs = async ($event) => {
    return {
      objectType: 'contract_service_fee',
      op: 'upsert',
      data: item.value,
    };
  };

  const act_7315092382398562_closeModal_2hhcw6ndu =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_2hhcw6ndu_getActionArgs,
    });
  const inst_evh_7315092382398562_close_15dmdii4t =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_2hhcw6ndu,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_15dmdii4t],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_4k04md3oh =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_a37olcixm =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_4k04md3oh,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_a37olcixm],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_jg9m2m713 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_yq6p592ar =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_jg9m2m713,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_yq6p592ar],
    event: 'close',
  });
  const act_1040167445267876_closeModal_1lez5ipyr =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_1lez5ipyr_getActionArgs,
    });
  const inst_evh_1040167445267876_success_c3ud5alwf =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_1lez5ipyr,
      event: 'success',
      when: inst_evh_1040167445267876_success_c3ud5alwf_getWhen,
      displayName: 'closeModal',
    });
  const act_1040167445267876_crud_hx79fb6da = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_hx79fb6da_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_c3ud5alwf],
  });
  const inst_evh_1040167445267876_submit_egsmhcbcz =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_hx79fb6da,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_egsmhcbcz],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_v9vnqigsn =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_v4frh4rpt =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_v9vnqigsn,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_v4frh4rpt],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_15dmdii4t,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_a37olcixm,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_yq6p592ar,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_c3ud5alwf,
    inst_evh_1040167445267876_submit_egsmhcbcz,
    save: inst_evh_1040167445267876_submit_egsmhcbcz,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_v4frh4rpt,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_15dmdii4t = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_15dmdii4t.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_a37olcixm = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_a37olcixm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_yq6p592ar = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_yq6p592ar.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_c3ud5alwf = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_c3ud5alwf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_egsmhcbcz = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_egsmhcbcz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_v4frh4rpt = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_v4frh4rpt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ContractServiceFee"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded && !noHeader"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" stretch v-if="loaded">
      <ItpForm
        :id="formId"
        :name="formName"
        class="row"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpCol>
          <ItpFormGroup
            name="contract"
            :label="pgettext('ContractServiceEditor', 'Contract')"
            :label-cols-md="3"
            :content-cols-md="9"
            :labelAlign="labelAlign"
            contentClass="form-control form-control-readonly"
            v-if="displayParent && item.contract"
          >
            <UcContractMiniCard :item="item.contract" no-link>
            </UcContractMiniCard>
          </ItpFormGroup>
          <ItpFormGroup
            name="contract"
            :label="pgettext('ContractServiceEditor', 'Service')"
            :label-cols-md="3"
            :content-cols-md="9"
            :labelAlign="labelAlign"
            contentClass="form-control form-control-readonly"
            v-if="displayParent && item.contract_service"
          >
            <span>
              {{ item.contract_service.name }}
            </span>
          </ItpFormGroup>
          <hr v-if="displayParent" />
          <FieldsetContractServiceFee :item="item">
          </FieldsetContractServiceFee>
          <div class="offset-3">
            <ItpButton
              :text="pgettext('Button', 'Save')"
              :disabled="!forms[formName] || !forms[formName].submittable"
              type="submit"
              :form="formId"
              variant="primary"
              icon="fa-save"
              :spinning="actions.save.isRunning"
              v-if="inlineCommands"
            >
            </ItpButton>
          </div>
        </ItpCol>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded && !noFooter">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          :disabled="!forms[formName] || !forms[formName].submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
